/**
 * Pure math for server-generated drawing tile pyramids (BM-13597, RFD00134),
 * described by a per-page manifest from `bim-pdf-parser`:
 *
 *   - 256px square tiles; level `maxLevel` = natural raster, each level below
 *     halves it; only `minLevel..maxLevel` are published (previews cover the
 *     shallow end).
 *   - Bing-style quadkey naming (0=TL, 1=TR, 2=BL, 3=BR, one digit per
 *     level); paths always come from the manifest's `tileUrlTemplate`.
 *   - Interior tiles are exact crops; edge tiles are the remainder STRETCHED
 *     to fill the tile (shared with the GLB pipeline), so a tile image always
 *     maps exactly onto {@link tileDocRect}.
 */

/** One published pyramid level, with its exact raster size in pixels. */
export type TileSetLevel = {
    level: number;
    width: number;
    height: number;
};

/** One pre-rendered preview image of the whole page. */
export type TileSetPreview = {
    name: string;
    /** Path relative to the tile set root (resolve via `buildTileSetFileUrl`). */
    path: string;
    width: number;
    height: number;
};

/** A drawing page's tile set, resolved from its manifest. */
export type TiledDrawingSource = {
    /**
     * Tile set root the manifest's relative paths resolve against. Either a
     * plain URL prefix or a signed FAS URL (query-style, see
     * `buildTileSetFileUrl`). Empty when the source was resolved purely from
     * OPFS while offline (no live URL was ever available) -- `fetchTileSetFile`
     * only reaches a fetch using this when the OPFS read-through misses, and
     * offline that fetch is expected to fail the same way an uncached tile
     * always has.
     */
    baseUrl: string;
    /**
     * Scene-file placement id (`platform_file_instance_id` from
     * `plans/extended`), present whenever this source came from the tiled
     * route rather than a dev override. Keys the OPFS path tile-set files are
     * journaled under (`minimap/{fileInstanceId}/...`, see `offlineStore.ts`).
     */
    fileInstanceId?: string;
    /** Natural (full-resolution) page size in pixels. */
    width: number;
    height: number;
    /** Tile edge length in pixels. */
    tileSize: number;
    /** Shallowest / deepest published level. */
    minLevel: number;
    maxLevel: number;
    /** Published levels with their exact raster sizes, ascending. */
    levels: TileSetLevel[];
    /** Relative tile path template containing `{quadkey}`. */
    tileUrlTemplate: string;
    /** Available previews, sorted ascending by longest side. */
    previews: TileSetPreview[];
};

export type TileGridSize = { cols: number; rows: number };

/**
 * Normalized (0..1) document-space rectangle covered by a tile. The full tile
 * image maps exactly onto this rectangle (edge tiles are stretched at encode
 * time, so no source cropping is ever needed).
 */
export type NormalizedRect = { x0: number; y0: number; x1: number; y1: number };

/** Raster size of a published level (from the manifest; exact, not derived). */
export function levelRasterSize(source: TiledDrawingSource, level: number): { width: number; height: number } {
    const entry = source.levels.find((l) => l.level === level);
    if (!entry) {
        throw new Error(`Level ${level} is not published in this tile set (${source.minLevel}..${source.maxLevel})`);
    }
    return { width: entry.width, height: entry.height };
}

/** Number of tile columns/rows at the given level. */
export function levelGridSize(source: TiledDrawingSource, level: number): TileGridSize {
    const raster = levelRasterSize(source, level);
    return {
        cols: Math.max(1, Math.ceil(raster.width / source.tileSize)),
        rows: Math.max(1, Math.ceil(raster.height / source.tileSize)),
    };
}

/**
 * Bing-maps-style quadkey for tile (x, y) at `level`. One digit per level,
 * most significant first; empty string for level 0.
 */
export function toQuadKey(x: number, y: number, level: number): string {
    let key = "";
    for (let i = level; i > 0; i--) {
        const mask = 1 << (i - 1);
        let digit = 0;
        if (x & mask) {
            digit += 1;
        }
        if (y & mask) {
            digit += 2;
        }
        key += digit;
    }
    return key;
}

/** Tile path relative to the tile set root, from the manifest's template. */
export function tileRelativePath(source: TiledDrawingSource, level: number, x: number, y: number): string {
    return source.tileUrlTemplate.replace("{quadkey}", toQuadKey(x, y, level));
}

/**
 * Every file of a tile set relative to its root: all preview tiers plus
 * every tile of every published level. Used by the full-sync download
 * (RFD00000145) to enumerate what "all of this drawing's mini map" means,
 * purely from the ~1 KB manifest — no server-side file listing needed.
 *
 * Does not include the manifest document itself: that's a naming convention
 * owned by `tiledSource.ts` (`TILE_MANIFEST_RELATIVE_PATH`), not a property
 * of the tile set's own math.
 */
export function allTileSetFilePaths(source: TiledDrawingSource): string[] {
    const paths: string[] = source.previews.map((preview) => preview.path);
    for (let level = source.minLevel; level <= source.maxLevel; level++) {
        const grid = levelGridSize(source, level);
        for (let y = 0; y < grid.rows; y++) {
            for (let x = 0; x < grid.cols; x++) {
                paths.push(tileRelativePath(source, level, x, y));
            }
        }
    }
    return paths;
}

/** Normalized document rectangle covered by tile (x, y) at `level`. */
export function tileDocRect(source: TiledDrawingSource, level: number, x: number, y: number): NormalizedRect {
    const raster = levelRasterSize(source, level);
    const ts = source.tileSize;
    return {
        x0: (x * ts) / raster.width,
        y0: (y * ts) / raster.height,
        x1: Math.min((x + 1) * ts, raster.width) / raster.width,
        y1: Math.min((y + 1) * ts, raster.height) / raster.height,
    };
}

/** Inclusive tile index range (may be empty when max < min). */
export type TileRange = { txMin: number; txMax: number; tyMin: number; tyMax: number };

/**
 * Tile index range of `level` intersecting the given normalized document
 * region (the region actually visible in the viewport). Single source of
 * truth for the visibility math — `visibleTileCount` (level selection) and
 * `drawTiledImage` (painting) must agree for the tile budget to be sound.
 */
export function visibleTileRange(source: TiledDrawingSource, level: number, region: NormalizedRect): TileRange {
    const raster = levelRasterSize(source, level);
    const grid = levelGridSize(source, level);
    const ts = source.tileSize;
    return {
        txMin: Math.max(0, Math.floor((region.x0 * raster.width) / ts)),
        txMax: Math.min(grid.cols - 1, Math.ceil((region.x1 * raster.width) / ts) - 1),
        tyMin: Math.max(0, Math.floor((region.y0 * raster.height) / ts)),
        tyMax: Math.min(grid.rows - 1, Math.ceil((region.y1 * raster.height) / ts) - 1),
    };
}

/**
 * Number of tiles of `level` that intersect the given normalized document
 * region (the region actually visible in the viewport).
 */
export function visibleTileCount(source: TiledDrawingSource, level: number, region: NormalizedRect): number {
    const { txMin, txMax, tyMin, tyMax } = visibleTileRange(source, level, region);
    return Math.max(0, txMax - txMin + 1) * Math.max(0, tyMax - tyMin + 1);
}

/**
 * Level for painting the visible `region` of a viewport spanning `targetPx`
 * device pixels. Rounds UP for sharpness (shallowest level covering the
 * target — rounding down would leave zoomed views up to 2x blurry and could
 * make the deepest level unreachable). Memory is guarded by the `tileBudget`
 * clamp: the level steps down while the visible region needs more tiles than
 * the budget. The clamp is best-effort — `minLevel` is returned even when its
 * visible count still exceeds the budget (a server may publish a shallowest
 * level whose grid is large), so the hard cap lives at the consumption side:
 * `drawTiledImage` never touches more than `tileBudget` tiles per paint.
 * Clamped to `minLevel..maxLevel`; below `minLevel` the preview base layer
 * covers.
 */
export function pickDrawLevel(
    source: TiledDrawingSource,
    targetPx: number,
    region: NormalizedRect,
    tileBudget: number,
): number {
    let level = source.maxLevel;
    for (let candidate = source.minLevel; candidate <= source.maxLevel; candidate++) {
        const raster = levelRasterSize(source, candidate);
        if (Math.max(raster.width, raster.height) >= targetPx) {
            level = candidate;
            break;
        }
    }
    while (level > source.minLevel && visibleTileCount(source, level, region) > tileBudget) {
        level--;
    }
    return level;
}

/**
 * Preview tier for a consumer that caps its decoded bitmap at `maxSize`:
 * the largest preview whose longest side does not exceed the cap (so the
 * decode stays within the consumer's memory budget), falling back to the
 * smallest available preview when even that is over the cap.
 */
export function pickPreview(source: TiledDrawingSource, maxSize: number): TileSetPreview | undefined {
    let picked: TileSetPreview | undefined;
    for (const preview of source.previews) {
        if (Math.max(preview.width, preview.height) <= maxSize) {
            picked = preview;
        }
    }
    return picked ?? source.previews[0];
}
